import { Alert, AsyncButton, Button, ColoredAlert, Label } from '@bpinternal/ui-kit-next'
import { ConfigSection } from './GeneralConfig'
import { CodeSnippet } from '~/components'
import { getUseConfigStore } from '../../stores/useConfigStore'
import { FC } from 'react'
import { config } from '~/shared'
import { Input } from '~/elements'
import { X } from 'lucide-react'
import { useFeature } from '~/hooks'
import { Plan } from '~/features/billing/types'

type Props = {
  workspaceId: string
  botId: string
  plan: Plan
}

export const AdvancedConfigSection: FC<Props> = ({ workspaceId, botId, plan }) => {
  const useConfigStore = getUseConfigStore({ botId, workspaceId })
  const webhookId = useConfigStore((state) => state.webhookId)
  const allowedOrigins = useConfigStore((state) => state.allowedOrigins)
  const adminSecret = useConfigStore((state) => state.adminSecret)

  const setAllowedOrigins = useConfigStore((state) => state.setAllowedOrigins)
  const setAdminSecret = useConfigStore((state) => state.setAdminSecret)

  const saveConfig = useConfigStore((state) => state.saveConfig)
  const _updating = useConfigStore((state) => state._updating)

  const adminSecretEnabled = useFeature('webchat-admin-key')

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-6">
        {webhookId && (
          <ConfigSection
            title="Client ID"
            description="The client ID is used to identify the webchat instance. You'll find it most useful when building with our react library or if you're building a custom frontend."
            linkUrl="https://botpress.com/docs/webchat/react-library/get-started/"
            linkLabel="Documentation"
          >
            <CodeSnippet code={webhookId} />
          </ConfigSection>
        )}
        <ConfigSection
          title="Allowed Origins"
          description="List of origins that are allowed to access the webchat. Leave an empty list to allow all origins."
        >
          {plan === 'community' || plan === 'plus' ? (
            <Alert className="text-center">
              <div className="mb-3">This feature is only available for Team and Enterprise plan.</div>
              <a href={`/workspaces/${workspaceId}/billing/plans`}>
                <Button variant="secondary" size="sm">
                  Upgrade Now
                </Button>
              </a>
            </Alert>
          ) : (
            <ColoredAlert color="amber" className="mb-2">
              You must include <b>{config.dashboardUrl}</b> to still be able to use the chat tab in the dashboard.
            </ColoredAlert>
          )}
          <div className="mb-4 flex flex-col gap-2">
            {plan !== 'community' &&
              allowedOrigins?.map((origin: string, index: number) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    placeholder="http://example.com"
                    value={origin}
                    onChange={(e) => {
                      const newOrigins = [...allowedOrigins]
                      newOrigins[index] = e.target.value
                      setAllowedOrigins(newOrigins)
                    }}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="aspect-square hover:bg-red-3 hover:text-red-10"
                    onClick={() => {
                      const newOrigins = [...allowedOrigins]
                      newOrigins.splice(index, 1)
                      setAllowedOrigins(newOrigins)
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
          </div>
          <div className="flex">
            {plan !== 'community' && plan !== 'plus' && (
              <Button
                variant="ghost"
                className="text-blue-10 hover:bg-blue-3 hover:text-blue-10"
                onClick={() => setAllowedOrigins([...(allowedOrigins ?? []), ''])}
              >
                Add Origin
              </Button>
            )}
          </div>
        </ConfigSection>
        {adminSecretEnabled && (
          <ConfigSection title="Admin Secret">
            <Label className="pb-2 text-muted-foreground">Admin secret to control user key generation</Label>
            <Input
              className="mt-1"
              placeholder="Enter admin secret"
              value={adminSecret}
              onChange={(e) => (e.target.value === '' ? setAdminSecret(undefined) : setAdminSecret(e.target.value))}
            />
          </ConfigSection>
        )}
      </div>
      <AsyncButton loading={_updating} className="self-end" onClick={saveConfig}>
        Save Configuration
      </AsyncButton>
    </div>
  )
}
